import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { getAlbumById } from '../../Services/ApiCalls';
import { AlbumWithPhotos } from '../../types';
import ImageCarousel, { CarouselImage } from '../../Components/ImageCarousel';
import Seo from '../../Components/Seo';

const Album: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [album, setAlbum] = useState<AlbumWithPhotos | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCarousel, setShowCarousel] = useState(false);
    const [carouselStartIndex, setCarouselStartIndex] = useState(0);

    useEffect(() => {
        const fetchAlbum = async () => {
            if (!id) {
                setError('Album ID is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const albumId = parseInt(id, 10);
                if (isNaN(albumId)) {
                    setError('Invalid album ID');
                    setLoading(false);
                    return;
                }

                const data = await getAlbumById(albumId);
                setAlbum(data);
            } catch (err) {
                console.error('Error fetching album:', err);
                setError('Failed to load album. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchAlbum();
    }, [id]);

    const handleImageClick = (index: number) => {
        setCarouselStartIndex(index);
        setShowCarousel(true);
    };

    const handleCloseCarousel = () => {
        setShowCarousel(false);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error || !album) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="error" sx={{ mb: 2 }}>
                    {error || 'Album not found'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    const photos: CarouselImage[] = album.photos?.map((photo) => ({
        id: photo.id,
        imageUrl: photo.imageUrl,
        imagedesc: photo.imagedesc,
        like: photo.like,
        share: photo.share,
    })) || [];
    const readableDescription = album.description && !/^https?:\/\//i.test(album.description.trim())
        ? album.description
        : `${album.title} is an educational radiology image collection from Radiology Illustration.`;

    return (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Seo
                title={album.title}
                description={readableDescription}
                canonicalPath={`/album/${album.id}`}
                image={album.coverImage}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'ImageGallery',
                    name: album.title,
                    description: readableDescription,
                    image: album.coverImage,
                    url: `https://radiologyillustration.com/album/${album.id}`,
                }}
            />
            {/* Back button and title */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{ minWidth: 'auto', fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    size="small"
                >
                    Back
                </Button>
                <Typography 
                    variant="h4" 
                    component="h1"
                    sx={{
                        fontSize: {
                            xs: '1.25rem',
                            sm: '1.75rem',
                            md: '2rem',
                        },
                        fontWeight: 600,
                    }}
                >
                    {album.title}
                </Typography>
            </Box>

            {/* Album description */}
            {album.description && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                    <Typography 
                        variant="body1"
                        sx={{
                            fontSize: {
                                xs: '0.9rem',
                                sm: '1rem',
                            },
                            lineHeight: 1.6,
                        }}
                    >
                        {album.description}
                    </Typography>
                </Paper>
            )}

            {/* Photos grid */}
            {photos.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        No images in this album yet
                    </Typography>
                </Paper>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(4, 1fr)',
                        },
                        gap: 2,
                    }}
                >
                    {photos.map((photo, index) => (
                        <Box
                            key={photo.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 2,
                                overflow: 'visible',
                                boxShadow: 2,
                                backgroundColor: 'background.paper',
                                height: '100%',
                            }}
                        >
                            {/* Image Container */}
                            <Box
                                onClick={() => handleImageClick(index)}
                                sx={{
                                    width: '100%',
                                    cursor: 'pointer',
                                    borderRadius: '8px 8px 0 0',
                                    overflow: 'hidden',
                                    aspectRatio: '1',
                                    flexShrink: 0,
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                        transition: 'transform 0.2s',
                                    },
                                }}
                            >
                                <img
                                    src={photo.imageUrl}
                                    alt={photo.imagedesc || `Image ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            </Box>
                            {/* Description Container - Below Image */}
                            {photo.imagedesc && (
                                <Box
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: 'background.paper',
                                        borderRadius: '0 0 8px 8px',
                                        flexGrow: 0,
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.primary',
                                            textAlign: 'left',
                                            wordBreak: 'break-word',
                                            fontSize: {
                                                xs: '0.8rem',
                                                sm: '0.875rem',
                                            },
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {photo.imagedesc}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Image count */}
            {photos.length > 0 && (
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 3, textAlign: 'center' }}
                >
                    {photos.length} {photos.length === 1 ? 'image' : 'images'} in this album
                </Typography>
            )}

            {/* Carousel */}
            {showCarousel && photos.length > 0 && (
                <ImageCarousel
                    images={photos}
                    initialIndex={carouselStartIndex}
                    onClose={handleCloseCarousel}
                    albumTitle={album.title}
                />
            )}
        </Box>
    );
};

export default Album;
